import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import pLimit from 'p-limit';
import { useCallback } from 'react';
import { useTextureAuth } from './textureAuthContext';

const isLocalDev = (process.env.NEXT_PUBLIC_STAGE_API ?? '').endsWith('-local');

const limit = isLocalDev ? pLimit(1) : (fn) => fn();

const instance = axios.create();

axiosRetry(instance, {
  retries: isLocalDev ? 3 : 3,
  retryDelay: axiosRetry.exponentialDelay,
});

let requestInterceptor;
let responseInterceptor;

// We wrap Axios methods in a hook, so we can centrally handle adding auth tokens.
export function useAxios() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { textureToken, isTextureAuth } = useTextureAuth();

  // The request interceptor is not constant, because it contains the access token, so we need to set it every
  // time we call this hook.
  if (requestInterceptor) {
    instance.interceptors.request.eject(requestInterceptor);
    requestInterceptor = undefined;
  }
  if (!requestInterceptor) {
    requestInterceptor = instance.interceptors.request.use(async (config) => {
      if (process.env.NEXT_PUBLIC_STAGE_APP === 'dev') {
        // Get relative path from absolute URL
        console.debug(
          'API request:  -->',
          config.method,
          config.url,
          config.params,
          config.data,
          );
      }

      if (config.url.indexOf('http') === -1) {
        config.url = `${process.env.NEXT_PUBLIC_API_ENDPOINT}${config.url}`;
      }

      // Use texture token if authenticated with texture token, otherwise use Auth0 token
      if (isTextureAuth && textureToken) {
        config.headers.Authorization = `Bearer ${textureToken}`;
      } else if (isAuthenticated) {
        let accessToken;
        try {
          accessToken = await getAccessTokenSilently();
        } catch (e) {
          console.debug('Failed to get access token:', e);
          // We can handle the error here by calling loginWithRedirect() which works but sends the user back
          // to the home page after login. Without these lines here, withAuthenticationRequired steps in with
          // a loginWithRedirect() call and successfully sends the user back to the same page they were on. So
          // we leave the error handling to withAuthenticationRequired.
          //
          // if (e.error === 'invalid_grant' || e.error === 'login_required') {
          //   loginWithRedirect({
          //     authorizationParams: { prompt: "login" },
          //   });
          //   throw e;
          // }
          throw e;
        }
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    }, (error) => {
      return Promise.reject(error);
    });
  }

  // THe response interceptor is constant, so we only need to set it once.
  if (!responseInterceptor) {
    responseInterceptor = instance.interceptors.response.use((response) => {
      if (process.env.NEXT_PUBLIC_STAGE_APP === 'dev') {
        console.debug(
          'API response: <--',
          response.config.method,
          new URL(response.config.url).pathname,
          response.status,
          response,
          );
      }
      return response;
    }, (error) => {
      if (process.env.NEXT_PUBLIC_STAGE_APP === 'dev') {
        console.debug('API error:   ', error);
      }

      // The Axios library will throw an error if the HTTP response status code indicates a client error (4xx)
      // or a server error (5xx).
      //
      // In our own API, we use the jsend specification to report failures. In some cases we expect client
      // errors, e.g. when querying for a userId that might not exist yet. So we return that response to the
      // caller instead of throwing an error.
      //
      // FIXME: If the API Gateway responds with a 40x code, we don't have a good way of detecting that error
      // from the result we are returning here.
      //
      // FIXME: It would be simpler to just use a try-catch for 401 errors around specific API calls where we
      // expect that the API might return an error, for example when a database item doesn't exist yet.

      const status = error.response?.status || 500;
      if (error.response && status >= 400 && status < 500) {
        return Promise.resolve(error.response);
      }

      return Promise.reject(error);
    });
  }

  return {
    apiGet: useCallback((url, config) => limit(() => instance.get(url, config)), []),
    apiPut: useCallback((url, data, config) => limit(() => instance.put(url, data, config)), []),
    apiPost: useCallback((url, data, config) => limit(() => instance.post(url, data, config)), []),
    apiPatch: useCallback((url, data, config) => limit(() => instance.patch(url, data, config)), []),
    apiDelete: useCallback((url, config) => limit(() => instance.delete(url, config)), []),
  }
};
